// @flow
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'src/hooks/useAppSelector';
import type { FieldProps, FormProps } from 'redux-form';

import {
  fetchOrganizationsWithSubset,
  selectOrganizationsFetching,
  type Organization,
  type Organizations,
} from '@gandi/redux-sharing';

import { Badge, Base, Text, Svg } from '@gandi/ui';
import { Custom } from '@gandi/ui-select';
import { useTranslator } from '@gandi/translate';

import {
  selectOrganizations,
  selectPersonalOrgaDetails,
} from '../../Support/modules/organization';
import styles from './FilterableSelect.scss';

type OwnProps = {|
  reduxFormProps: FormProps,
|};

type Props = {| ...$Exact<FieldProps>, ...OwnProps |};

function RenderOption(orga: Organization): React.Element<typeof Base> {
  const { __ } = useTranslator();

  const personalDetails = useAppSelector(selectPersonalOrgaDetails);

  return (
    <Base
      flex
      flexAlignItemsCenter
      gap="xsmall"
      paddingX="small"
      paddingY="xsmall"
    >
      <Text weight="semibold">{orga.name}</Text>
      {orga.id === personalDetails.id && (
        <>
          {' '}
          <Badge>{__('Me')}</Badge>
        </>
      )}
      {orga.reseller && (
        <Svg
          id={`${orga.id}-reseller`}
          className={styles.icon}
          title={__('Reseller')}
          name="badgeReseller"
          theme="anthracite"
        />
      )}
      {orga.corporate && (
        <Svg
          id={`${orga.id}-corporate`}
          className={styles.icon}
          title={__('Corporate')}
          name="badgeCorporate"
          theme="anthracite"
        />
      )}
    </Base>
  );
}

function FilterableSelect({
  reduxFormProps,
}: Props): React.Element<typeof Custom> {
  const dispatch = useDispatch();

  const [value, setValue] = React.useState<?Organization>();

  const isFetching = useAppSelector(state =>
    selectOrganizationsFetching(state, 'filtered')
  );

  const organizations = useAppSelector(selectOrganizations);

  const getOrganizations: (
    input: string,
    actionMeta: {|
      action: string,
    |}
  ) => Promise<Organizations> | void = (input, actionMeta) => {
    if (
      actionMeta.action === 'input-change' ||
      (actionMeta.action === 'input-blur' && !input)
    ) {
      dispatch(
        fetchOrganizationsWithSubset(
          typeof input === 'string' && input !== ''
            ? `~name=*${input}*`
            : '~name='
        )
      );
    }
  };

  const onChange: (newOption: Organization) => mixed = React.useCallback(
    newOption => {
      setValue(newOption);

      reduxFormProps.change('reseller', newOption.reseller);
      reduxFormProps.change('orgname', newOption.name);
      reduxFormProps.change('sharing_id', newOption.id);
    },
    [reduxFormProps, setValue]
  );

  return (
    <Custom
      isSearchable
      // $FlowFixMe[incompatible-type]
      // $FlowFixMe[incompatible-indexer]
      // $FlowFixMe[incompatible-variance]
      onChange={onChange}
      options={organizations}
      // $FlowFixMe[incompatible-indexer]
      // $FlowFixMe[incompatible-variance]
      renderOption={RenderOption}
      onInputChange={getOrganizations}
      isLoading={isFetching}
      getOptionValue={(option: Organization) => option.id}
      getOptionLabel={(option: Organization) => option.name}
      value={value}
    />
  );
}

export default FilterableSelect;
